import { useState, useCallback, useRef } from 'react'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import type { Driver, PopoverDOM, Config } from 'driver.js'
import type { TourDefinition, TourStep, TourUserRole } from '../types'
import { tourProgressService, tourAnalyticsService } from '../services/tourService'

const AI_BTN_CLASS = 'vharta-tour-ai-btn'

export function buildDriverSteps(steps: TourStep[], role: string | null): TourStep[] {
  return steps.filter((step) => {
    if (step.roles && role && !step.roles.includes(role as TourUserRole)) return false
    // TourStep.optional (opt-in): leave out a step whose element isn't on the
    // page when the tour starts, instead of showing it against nothing.
    if (step.optional && typeof step.element === 'string' && !document.querySelector(step.element)) {
      return false
    }
    return true
  })
}

function injectAIButton(
  popover: PopoverDOM,
  aiContext: string | undefined,
  onAIClick: (context: string) => void,
): void {
  if (!aiContext) return

  const existing = popover.description.querySelector(`.${AI_BTN_CLASS}`)
  if (existing) existing.remove()

  const btn = document.createElement('button')
  btn.className = AI_BTN_CLASS
  btn.setAttribute('data-context', aiContext)
  btn.innerHTML = '&#10024; Ask AI'
  btn.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    onAIClick(aiContext)
  })
  popover.description.appendChild(btn)
}

interface UseTourReturn {
  startTour: (tour: TourDefinition, onAIClick: (context: string) => void) => void
  stopTour: () => void
  isRunning: boolean
  currentStep: number
}

export function useTour(
  userRole: string | null,
  onCompleted: (tourId: string) => void,
): UseTourReturn {
  const [isRunning, setIsRunning] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const driverRef = useRef<Driver | null>(null)
  const activeTourIdRef = useRef<string | null>(null)

  const stopTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy()
      driverRef.current = null
    }
    setIsRunning(false)
    setCurrentStep(0)
    activeTourIdRef.current = null
  }, [])

  const startTour = useCallback(
    (tour: TourDefinition, onAIClick: (context: string) => void) => {
      if (driverRef.current) {
        driverRef.current.destroy()
      }

      const filteredSteps = buildDriverSteps(tour.steps, userRole)
      if (!filteredSteps.length) return

      tourProgressService.markStarted(tour.id)
      tourAnalyticsService.track({ tourId: tour.id, action: 'started' })
      activeTourIdRef.current = tour.id

      const config: Config = {
        showProgress: true,
        animate: true,
        allowClose: true,
        overlayOpacity: 0.7,
        smoothScroll: true,
        onDestroyStarted: () => {
          const d = driverRef.current
          if (!d) return
          const idx = d.getActiveIndex() ?? 0
          const isLast = d.isLastStep()
          if (isLast) {
            onCompleted(tour.id)
            tourAnalyticsService.track({ tourId: tour.id, action: 'completed' })
          } else {
            tourAnalyticsService.track({
              tourId: tour.id,
              action: 'skipped',
              stepIndex: idx,
            })
          }
          d.destroy()
          driverRef.current = null
          setIsRunning(false)
          setCurrentStep(0)
          activeTourIdRef.current = null
        },
        steps: filteredSteps.map((step, idx) => ({
          ...step,
          popover: {
            ...step.popover,
            onPopoverRender: (popover: PopoverDOM) => {
              injectAIButton(popover, step.aiContext, (ctx) => {
                onAIClick(ctx)
                tourAnalyticsService.track({
                  tourId: tour.id,
                  action: 'ai_hint_requested',
                  stepIndex: idx,
                  stepTitle: step.popover?.title,
                })
              })
            },
            onNextClick: () => {
              setCurrentStep((prev) => prev + 1)
              tourProgressService.updateLastStep(tour.id, idx + 1)
              tourAnalyticsService.track({
                tourId: tour.id,
                action: 'step_viewed',
                stepIndex: idx,
                stepTitle: step.popover?.title,
              })
              driverRef.current?.moveNext()
            },
          },
        })),
      }

      const d = driver(config)
      driverRef.current = d
      setIsRunning(true)
      setCurrentStep(0)
      d.drive()
    },
    [userRole, onCompleted],
  )

  return { startTour, stopTour, isRunning, currentStep }
}
